import React, { useEffect, useRef, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import CloseIcon from 'icons/ic-close.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';
import voca from 'voca';

import CheckboxFields from 'components/application/CheckboxFields';
import { Option } from 'components/application/CollectionSelect';

import { useBreadBoard } from 'components/contexts/Toaster';

import { getNextPageParam } from 'components/helpers/reactQuery';

const moreSelectedResourcesBreakpoint = 3;

export default function MultiSelectFilter({
  autoFocus = false,
  checkboxes = [],
  fieldError,
  id,
  label,
  message,
  name,
  nullResource,
  onDeselect,
  onFocus,
  onRemove,
  onSelect,
  params = {},
  placeholder,
  resourceLabel,
  resourceSort,
  selectedIds,
  type,
}) {
  const breadBoard = useBreadBoard();

  const [searchText, setSearchText] = useState('');

  const [selectableResourcesEnabled, setSelectableResourcesEnabled] =
    useState(false);

  const [selectedResources, setSelectedResources] = useState([]);

  const [showMoreSelectedResources, setShowMoreSelectedResources] =
    useState(false);

  const setSearchTextDebounced = useRef(
    _.debounce((searchText) => setSearchText(searchText), 500),
  ).current;

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'filters.MultiSelectFilter',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  // a namespaced type (e.g. `projects_dashboards/configurable_field`) keeps
  // its namespace in the url but not in the param key
  const paramKey = `${type.split('/').pop()}_search`;
  const url = `/${type}_searches`;

  const selectableResourcesParams = { search: searchText, filter: {} };

  if (nullResource || selectedIds.length > 0) {
    selectableResourcesParams.filter.ids = {
      nin: nullResource ? [...new Set([...selectedIds, null])] : selectedIds,
    };
  }

  const fetchSelectableResources = (page) =>
    axios.post(url, {
      [paramKey]: { ...params, ...selectableResourcesParams, page, per: 25 },
    });

  const {
    data: selectableResources,
    fetchNextPage: selectableResourcesFetchNextPage,
    hasNextPage: selectableResourcesHasNextPage,
    isFetching: selectableResourcesIsFetching,
  } = useInfiniteQuery({
    queryKey: [
      `selectable_${voca.snakeCase(label)}`,
      selectableResourcesParams,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const selectableResourcesResponse =
        await fetchSelectableResources(pageParam);

      return selectableResourcesResponse.data;
    },
    getNextPageParam,
    enabled: selectableResourcesEnabled || !!searchText,
  });

  const resourceOption = (resource) => ({
    label: resourceLabel(resource),
    record: resource,
    value: resource.id,
  });

  const selectableResourceOptions = (resourceData) => {
    const resourceDataOptions = resourceData.pages
      .map((page) => page.data.map((resource) => resourceOption(resource)))
      .flat();

    return !!nullResource && !selectedIds.some((id) => id === null) ?
        [resourceOption(nullResource), ...resourceDataOptions]
      : resourceDataOptions;
  };

  const handleFilterSelection = ({ record }) => {
    onSelect(record.id);

    const newSelectedResources = [resourceOption(record), ...selectedResources];
    setSelectedResources(newSelectedResources);
  };

  const handleFilterDeselection = ({ record }) => {
    onDeselect(record.id);

    const newSelectedResources = selectedResources.filter(
      (selectedResource) => selectedResource.record.id !== record.id,
    );
    setSelectedResources(newSelectedResources);
  };

  const visibleSelectedResources =
    (
      selectedResources.length < moreSelectedResourcesBreakpoint ||
      showMoreSelectedResources
    ) ?
      selectedResources
    : selectedResources.slice(0, moreSelectedResourcesBreakpoint);

  const fetchSelectedResources = () => {
    axios
      .post(url, {
        [paramKey]: {
          ...params,
          filter: {
            ids: {
              in: nullResource ? selectedIds.filter((id) => id) : selectedIds,
            },
          },
        },
      })
      .then((response) => {
        const resourceDataOptions = response.data.data
          .map((resource) => resourceOption(resource))
          .sort(resourceSort);

        setSelectedResources(
          !!nullResource && selectedIds.some((id) => id === null) ?
            [resourceOption(nullResource), ...resourceDataOptions]
          : resourceDataOptions,
        );
      })
      .catch(breadBoard.addInedibleToast);
  };

  useEffect(() => {
    if (selectedIds.length === 0) return;

    if (nullResource && _.isEqual(selectedIds, [null])) {
      setSelectedResources([resourceOption(nullResource)]);
    } else {
      fetchSelectedResources();
    }
  }, []);

  return (
    <div onFocus={onFocus}>
      <div className='tw-mb-4 tw-flex tw-items-center tw-justify-between'>
        <label className='tw-m-0 tw-font-medium'>{label}</label>
        <div
          className='tw-flex tw-h-5 tw-cursor-pointer tw-items-center tw-rounded-lg tw-border-0 tw-bg-white tw-px-2 tw-text-grey-500 hover:tw-bg-red-025 hover:tw-text-red-500'
          onClick={onRemove}
        >
          <span className='tw-text-s tw-font-medium tw-tracking-wide'>
            {i18nTShared('removeFilter')}
          </span>
        </div>
      </div>
      {message && (
        <p className='tw-m-0 tw-mb-4 tw-text-s tw-font-normal tw-tracking-wide'>
          {message}
        </p>
      )}
      <Select
        autoFocus={autoFocus}
        className={classNames(
          'collection-select__select-container collection-select--transparent-option',
          !!fieldError?.fieldHighlighted && 'collection-select--invalid',
        )}
        classNamePrefix='collection-select'
        components={{ Option, DropdownIndicator, Input }}
        controlShouldRenderValue={false}
        filterOption={() => true}
        id={id}
        isLoading={selectableResourcesIsFetching}
        loadingMessage={() => null}
        name={name}
        noOptionsMessage={() => {
          searchText ? i18nTShared('noMatchingResults') : null;
        }}
        onChange={handleFilterSelection}
        onDropdownIndicatorOpen={() => setSelectableResourcesEnabled(true)}
        onInputChange={setSearchTextDebounced}
        onMenuClose={() => setSelectableResourcesEnabled(false)}
        onMenuScrollToBottom={() => {
          if (selectableResourcesHasNextPage) {
            selectableResourcesFetchNextPage();
          }
        }}
        openMenuOnClick={false}
        openMenuOnFocus={false}
        options={
          selectableResources ?
            selectableResourceOptions(selectableResources)
          : []
        }
        placeholder={placeholder}
        styles={{
          menu: (base) => ({ ...base, maxHeight: 244 }),
          menuList: (base) => ({ ...base, maxHeight: 242 }),
        }}
        value={null}
      />
      <ul
        className={classNames(
          'tw-m-0 tw-flex tw-flex-col tw-p-0',
          selectedResources.length > 0 && 'tw-mt-2',
        )}
      >
        {visibleSelectedResources.length > 0 &&
          visibleSelectedResources.map((selected) => (
            <li
              className='tw-relative tw-mt-2 tw-flex tw-min-h-[36px] tw-items-center tw-justify-between tw-rounded-lg tw-border-0 tw-bg-grey-050 tw-py-2 tw-pl-3 tw-pr-9 tw-text-grey-700'
              key={selected.value}
            >
              <div className='tw-text-s tw-font-medium tw-tracking-wide'>
                {selected.label}
              </div>
              <div
                className='tw-group tw-absolute tw-bottom-0 tw-right-1 tw-top-0 tw-m-[auto_0] tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center'
                onClick={() => handleFilterDeselection(selected)}
              >
                <div className='tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-grey-050 group-hover:tw-bg-grey-200'>
                  <CloseIcon
                    className='[&_polygon]:tw-fill-grey-500'
                    height={24}
                    width={24}
                  />
                </div>
              </div>
            </li>
          ))}
        {selectedResources.length > moreSelectedResourcesBreakpoint &&
          !showMoreSelectedResources && (
            <div className='tw-mt-4'>
              <button
                className='app-link tw-bg-transparent tw-align-baseline tw-text-s tw-font-medium tw-tracking-wide tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                onClick={() => setShowMoreSelectedResources(true)}
              >
                {i18nT('showMore', {
                  count:
                    selectedResources.length - moreSelectedResourcesBreakpoint,
                })}
              </button>
            </div>
          )}
      </ul>
      {checkboxes.length !== 0 && (
        <div className='tw-mt-4'>
          <CheckboxFields
            checkboxes={checkboxes}
            inline={true}
            minChecked={0}
          />
        </div>
      )}
    </div>
  );
}

MultiSelectFilter.propTypes = {
  autoFocus: PropTypes.bool,
  checkboxes: PropTypes.array,
  fieldError: PropTypes.object,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  message: PropTypes.string,
  name: PropTypes.string.isRequired,
  nullResource: PropTypes.object,
  onDeselect: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  resourceLabel: PropTypes.func.isRequired,
  resourceSort: PropTypes.func.isRequired,
  selectedIds: PropTypes.array.isRequired,
  type: PropTypes.string.isRequired,
};

function DropdownIndicator(props) {
  const chevronClassName =
    props.isDisabled ?
      '[&_polygon]:tw-fill-grey-300'
    : '[&_polygon]:tw-fill-grey-700';

  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ?
          <ChevronUpIcon className={chevronClassName} height={24} width={24} />
        : <ChevronDownIcon
            className={chevronClassName}
            height={24}
            onMouseDown={props.selectProps.onDropdownIndicatorOpen}
            onTouchEnd={props.selectProps.onDropdownIndicatorOpen}
            width={24}
          />
        }
      </components.DropdownIndicator>
    )
  );
}

// custom component required in order to stop the input from being hidden when selection is made
function Input(props) {
  return <components.Input {...props} isHidden={false} />;
}
